import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { authService } from "../../services/auth.service";
import { tagOf } from "../../utils/api-error";
import { forgotSchema } from "./validations/auth.schema";

// Tags this screen has copy for; anything else falls back to the generic line.
const KNOWN_TAGS = ["email_not_found", "rate_limited"];

const ForgotPassword = () => {
  const { t } = useTranslation(["auth", "common"]);
  const [sent, setSent] = useState(false);
  const [failureTag, setFailureTag] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(forgotSchema) });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFailureTag(null);
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch (err) {
      // email_not_found is now a real answer: the API rejects an address with
      // no account rather than pretending a code was sent.
      setFailureTag(tagOf(err) ?? "generic");
    }
  });

  if (sent) {
    return (
      <div className="card p-8 max-w-md mx-auto mt-10">
        <h1 className="mb-4 font-display text-2xl font-extrabold">
          {t("forgot.title")}
        </h1>
        <p className="mb-6 text-sm text-ink-soft">{t("forgot.sent")}</p>
        <Link to="/reset-password" className="font-bold text-teal-dark underline">
          {t("reset.submit")}
        </Link>
      </div>
    );
  }

  return (
    <div className="card p-8 max-w-md mx-auto mt-10">
      <h1 className="mb-2 font-display text-2xl font-extrabold">
        {t("forgot.title")}
      </h1>
      <p className="mb-6 text-sm text-ink-soft">{t("forgot.body")}</p>

      {failureTag && (
        <p
          role="alert"
          className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {KNOWN_TAGS.includes(failureTag)
            ? t(`errors.${failureTag}`)
            : t("common:errors.generic")}
        </p>
      )}

      <form onSubmit={onSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-bold">
            {t("fields.email")}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="field-input"
            {...register("email")}
          />
          {errors.email?.message && (
            <p className="mt-1 text-sm text-red-600">
              {t(errors.email.message)}
            </p>
          )}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn w-full">
          {t("forgot.submit")}
        </button>
      </form>
    </div>
  );
};

export default ForgotPassword;
